/**
 * SECURITY UPDATE: May 2025
 * 
 * This implementation processes Strapi media URLs to ensure they are properly handled
 * without exposing sensitive backend URLs in client-side code.
 * 
 * IMPORTANT: There are two ways to handle Strapi media in this application:
 * 
 * 1. Using regular <img> tags: We process the URL through useStrapiMedia
 *    to go through our proxy at /api/strapi/media
 * 
 * 2. Using <NuxtImg provider="strapi">: The Nuxt Image module handles this differently
 *    and requires ONLY the path portion, as the baseURL is set in nuxt.config.ts
 *    In this case, DO NOT use useStrapiMedia with the src prop.
 * 
 * @param url The URL or path to the media asset
 * @param forNuxtImg Optional flag for compatibility with NuxtImg (not recommended)
 * @returns A securely handled URL to the media asset
 */
export const getLocalStrapiMediaProxyPath = (url: string | null | undefined, forNuxtImg = false): string => {
  // Handle empty values gracefully
  if (!url) return '';
  
  // Handle null/undefined object serialization to prevent [object Object] in URL
  if (typeof url !== 'string') {
    console.error('[getLocalStrapiMediaProxyPath] Non-string URL provided:', url);
    return '';
  }
  
  // If the URL is already absolute, return it directly
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Special case for NuxtImg with provider="strapi"
  // This is not recommended - prefer to use NuxtImg WITHOUT useStrapiMedia
  if (forNuxtImg) {
    // NuxtImg with provider="strapi" needs just the path without leading slash
    return url.startsWith('/') ? url.substring(1) : url;
  }
  
  // Normal handling for regular <img> tags or situations where we need the full proxy URL
  // Ensure leading slash for path consistency
  const path = url.startsWith('/') ? url : `/${url}`;
  
  // Return the proxied URL through our secure server endpoint
  return `/api/strapi/media${path}`;
};

// Legacy alias for backward compatibility
export const useCustomStrapiMedia = getLocalStrapiMediaProxyPath;
